import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { applyLang, getCurrentLang } from '../utils/i18n';
import { commercesApi, COMMERCE_CATEGORIES, CATEGORY_LABEL } from '../services/commercesApi';
import './Annuaire.css';

const QUICK_LINKS = ['Thieboudiène', 'Maison d\'hôte', 'Atelier couture', 'Pirogue', 'Boutique tassou'];

const CAT_CARDS_META = [
  { id: 'restaurant',  cls: 'restaurant',  glyphText: 'R', desc: 'Cuisine sénégalaise, poisson frais, tables familiales.' },
  { id: 'boutique',    cls: 'boutique',    glyphText: 'B', desc: "Tissus, épices, livres, produits locaux — l'essentiel." },
  { id: 'artisan',     cls: 'artisan',     glyphText: 'A', desc: 'Couture traditionnelle, broderie, menuiserie, ferronnerie.' },
  { id: 'service',     cls: 'service',     glyphText: 'S', desc: 'Pirogues, cyber, télé, banque mobile — la logistique du village.' },
  { id: 'hebergement', cls: 'hebergement', glyphText: 'H', desc: "Maisons d'hôte face mer, locations courte durée, villas." },
  { id: 'marche',      cls: 'marche',      glyphText: 'M', desc: 'Le marché aux poissons et les étals du quotidien.' },
];

const MAP_COLOR = {
  restaurant: '#E76F51', boutique: '#C9A961', artisan: '#E2CB91',
  service: '#7DB3C5', hebergement: '#1B6985', marche: '#5A5048',
};
const MAP_POSITIONS = {
  restaurant:  { cx: 120, cy: 220, r: 6 },
  boutique:    { cx: 200, cy: 180, r: 5 },
  artisan:     { cx: 260, cy: 250, r: 5 },
  service:     { cx: 170, cy: 340, r: 5 },
  hebergement: { cx: 300, cy: 200, r: 5 },
  marche:      { cx: 100, cy: 320, r: 5 },
};

const Annuaire = () => {
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [commerces, setCommerces] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });
    document.querySelectorAll('.ann-page .reveal').forEach((el) => io.observe(el));
    applyLang(getCurrentLang());
    return () => io.disconnect();
  });

  // Fetch tous les commerces publiés une fois ; le filtrage est client-side
  useEffect(() => {
    let cancelled = false;
    commercesApi.list({ limit: 200 })
      .then((data) => { if (!cancelled) { setCommerces(data.items); setLoading(false); } })
      .catch(() => { if (!cancelled) { setCommerces([]); setLoading(false); } });
    return () => { cancelled = true; };
  }, []);

  // Compteurs par catégorie (live)
  const counts = useMemo(() => {
    const c = { all: commerces.length };
    for (const b of commerces) c[b.category] = (c[b.category] || 0) + 1;
    return c;
  }, [commerces]);

  const featured = useMemo(() => commerces.filter((c) => c.featured).slice(0, 3), [commerces]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return commerces.filter((b) => {
      if (filter !== 'all' && b.category !== filter) return false;
      if (q && !(`${b.name} ${b.description} ${b.address}`.toLowerCase().includes(q))) return false;
      return true;
    });
  }, [filter, query, commerces]);

  // CATS pour les chips et selects, avec compteurs dynamiques
  const CATS = COMMERCE_CATEGORIES.map((c) => ({ ...c, count: counts[c.id] || 0 }));

  // Cards catégorie avec compteurs réels
  const CAT_CARDS = CAT_CARDS_META.map((c) => ({
    ...c,
    title: CATEGORY_LABEL[c.id] || c.id,
    count: counts[c.id] || 0,
  }));

  // Map points avec compteurs et couleurs
  const MAP_POINTS = CAT_CARDS_META.map((c) => ({
    ...MAP_POSITIONS[c.id],
    color: MAP_COLOR[c.id],
    label: CATEGORY_LABEL[c.id],
    sub: `${counts[c.id] || 0} adresse${(counts[c.id] || 0) > 1 ? 's' : ''}`,
    id: c.id,
  }));

  const onSearch = (e) => {
    e.preventDefault();
    // Scroll vers la liste après recherche
    document.getElementById('biz-list')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <main className="ann-page">
      {/* ============== HERO ============== */}
      <header className="ann-hero">
        <div className="ann-hero-bg" />
        <div className="ann-hero-grain" />

        <div className="ann-hero-meta">
          <div><b>142</b><span>Commerces référencés</span></div>
          <div><b>6</b><span>Catégories</span></div>
          <div><b>100%</b><span>Vérifiés communauté</span></div>
        </div>

        <div className="wrap ann-hero-inner reveal">
          <span className="ann-hero-eyebrow">Annuaire local · Tenu par les habitants</span>
          <h1>
            Le village<br />
            <span className="ital">vous accueille.</span>
          </h1>
          <p className="lede">
            142 commerces, ateliers et services référencés et vérifiés par la communauté.
            Soutenir l'économie locale, c'est commencer ici.
          </p>

          <form className="ann-search" onSubmit={onSearch} role="search">
            <span className="ic" aria-hidden="true" />
            <input
              type="text"
              placeholder="Chercher un commerce, un plat, un service…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label="Recherche"
            />
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              aria-label="Catégorie"
            >
              {CATS.map((c) => <option key={c.id} value={c.id}>{c.lbl}</option>)}
            </select>
            <button type="submit">Trouver</button>
          </form>

          <div className="ann-hero-quicks">
            {QUICK_LINKS.map((q) => (
              <button
                key={q}
                className="ann-quick"
                onClick={() => { setQuery(q); onSearch({ preventDefault: () => {} }); }}
              >
                {q}
              </button>
            ))}
          </div>
        </div>
      </header>

      {/* ============== COUPS DE CŒUR ============== */}
      <section className="ann-featured">
        <div className="wrap">
          <div className="ann-head reveal">
            <div>
              <span className="eyebrow">Coups de cœur · Mai 2026</span>
              <h2>Trois adresses<br /><span className="ital">à ne pas manquer.</span></h2>
            </div>
            <a href="#biz-list" className="seeall">Voir tout l'annuaire →</a>
          </div>

          {featured.length === 0 && !loading && (
            <p style={{ textAlign: 'center', padding: '40px 0', color: 'var(--char-500)' }}>
              Aucun coup de cœur pour l'instant.
            </p>
          )}

          {featured.length > 0 && (
            <div className="fea-grid">
              {featured.map((f) => {
                const img = f.imageUrl || `https://picsum.photos/seed/${f.imageSeed || `cb-ann-${f.id}`}/1200/1500`;
                return (
                  <Link key={f.id} to="#biz-list" className={`fea-card ${f.category} reveal`}>
                    <div className="img" style={{ backgroundImage: `url('${img}')` }} />
                    <div className="grad" />
                    <span className="badge">Coup de cœur</span>
                    <div className="body">
                      <span className="cat">{CATEGORY_LABEL[f.category] || f.category}</span>
                      <h3>{f.name}</h3>
                      {f.rating > 0 && <div className="rate">★ {f.rating.toFixed(1)} · {f.reviews} avis</div>}
                      {f.description && <p>{f.description}</p>}
                      {f.address && <div className="addr">📍 {f.address}</div>}
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* ============== FILTRES STICKY ============== */}
      <div className="ann-filters">
        <div className="wrap">
          <span className="lab">Catégories —</span>
          {CATS.map((c) => (
            <button
              key={c.id}
              className={`ann-chip${filter === c.id ? ' on' : ''}`}
              onClick={() => setFilter(c.id)}
            >
              {c.lbl}
              <span className="ct">{c.count}</span>
            </button>
          ))}
          <button className="sort">↓ Mieux notés</button>
        </div>
      </div>

      {/* ============== GRID DE COMMERCES ============== */}
      <section className="ann-list" id="biz-list">
        <div className="wrap">
          <div className="ann-head reveal" style={{ marginBottom: 28 }}>
            <div>
              <span className="eyebrow">{visible.length} résultat{visible.length > 1 ? 's' : ''}</span>
              <h2>L'annuaire <span className="ital">de Cambérène.</span></h2>
            </div>
          </div>

          {loading && (
            <p style={{ textAlign: 'center', padding: '60px 0', color: 'var(--char-500)', fontFamily: 'var(--mono)', fontSize: 12, letterSpacing: '.18em', textTransform: 'uppercase' }}>
              Chargement de l'annuaire…
            </p>
          )}

          {!loading && visible.length === 0 && (
            <p style={{ textAlign: 'center', padding: '60px 0', color: 'var(--char-500)' }}>
              Aucun commerce ne correspond. Essayez un autre mot-clé.
            </p>
          )}

          {!loading && visible.length > 0 && (
            <div className="biz-grid-x">
              {visible.map((b) => {
                const isOpen = b.hours && !/(ferm|sur réservation|sur reservation)/i.test(b.hours);
                const imgUrl = b.imageUrl || `https://picsum.photos/seed/${b.imageSeed || `cb-ann-${b.id}`}/1200/800`;
                return (
                  <article key={b.id} className="biz-x reveal">
                    <div className="ph">
                      <div className="img" style={{ backgroundImage: `url('${imgUrl}')` }} />
                      <span className={`cat ${b.category}`}>{CATEGORY_LABEL[b.category] || b.category}</span>
                      {b.hours && (
                        <span className={`open ${isOpen ? 'now' : 'closed'}`}>
                          {isOpen ? `Ouvert · ${b.hours}` : b.hours}
                        </span>
                      )}
                    </div>
                    <div className="body">
                      <div className="top-row">
                        <h4>{b.name}{b.featured && <span style={{ color: 'var(--sunset)', marginLeft: 6, fontSize: 14 }}>★</span>}</h4>
                        {b.rating > 0 && (
                          <span className="rate"><b>★ {b.rating.toFixed(1)}</b>({b.reviews})</span>
                        )}
                      </div>
                      {b.description && <p className="desc">{b.description}</p>}
                      {b.address && <div className="addr">📍 {b.address}</div>}
                      <div className="actions">
                        <button className="btn-mini-x">Itinéraire</button>
                        <button className="btn-mini-x primary">
                          {b.category === 'restaurant' || b.category === 'hebergement' ? 'Réserver' :
                           b.category === 'service' ? 'Booker' : 'Contacter'}
                        </button>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* ============== CARTE ============== */}
      <section className="ann-map">
        <div className="wrap">
          <div className="ann-head reveal">
            <div>
              <span className="eyebrow">Plan du village</span>
              <h2>Tout est<br /><span className="ital">à 10 minutes à pied.</span></h2>
            </div>
            <a href="#biz-list" className="seeall">Voir la liste →</a>
          </div>

          <div className="map-shell">
            <div className="map-svg reveal">
              <span className="badge">⌖ Cambérène · 14.78°N · 17.45°O</span>
              <svg viewBox="0 0 400 480" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
                <defs>
                  <radialGradient id="ann-ocean" cx="50%" cy="40%" r="60%">
                    <stop offset="0%" stopColor="#1F6E84" />
                    <stop offset="100%" stopColor="#0A2330" />
                  </radialGradient>
                  <pattern id="ann-grid" width="32" height="32" patternUnits="userSpaceOnUse">
                    <path d="M 32 0 L 0 0 0 32" fill="none" stroke="rgba(250,246,238,.05)" strokeWidth="1" />
                  </pattern>
                </defs>
                <rect width="400" height="480" fill="url(#ann-ocean)" />
                <rect width="400" height="480" fill="url(#ann-grid)" />
                <path
                  d="M0 130 C 80 140, 130 180, 180 200 S 280 280, 320 320 S 380 420, 400 460 L 400 480 L 0 480 Z"
                  fill="rgba(232,220,196,.08)" stroke="rgba(232,220,196,.5)" strokeWidth="1.5"
                />
                {MAP_POINTS.map((p, i) => (
                  <g key={i}>
                    <circle cx={p.cx} cy={p.cy} r={p.r} fill={p.color} />
                    <circle cx={p.cx} cy={p.cy} r={p.r + 6} fill="none" stroke={p.color} strokeOpacity=".35" />
                  </g>
                ))}
              </svg>
            </div>

            <div className="map-side">
              {MAP_POINTS.map((p) => (
                <div key={p.label} className="map-pill reveal">
                  <span className="dot" style={{ background: p.color }} />
                  <div className="info">
                    <b>{p.label}</b>
                    <small>{p.sub}</small>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============== PAR CATÉGORIE ============== */}
      <section className="ann-cats">
        <div className="wrap">
          <div className="ann-head reveal">
            <div>
              <span className="eyebrow">Toutes les rubriques</span>
              <h2>Six familles,<br /><span className="ital">142 adresses.</span></h2>
            </div>
          </div>

          <div className="cat-grid">
            {CAT_CARDS.map((c) => (
              <div
                key={c.id}
                className={`cat-card ${c.cls} reveal`}
                onClick={() => {
                  setFilter(c.id);
                  document.getElementById('biz-list')?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                <span className="glyph">{c.glyphText}</span>
                <h4>{c.title}</h4>
                <p>{c.desc}</p>
                <div className="foot">
                  <b>{c.count}</b>
                  <span className="arr">Voir tout →</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============== CTA RÉFÉRENCEMENT ============== */}
      <section className="ann-cta">
        <div className="wrap">
          <div className="ann-cta-grid reveal">
            <div>
              <span className="eyebrow">Référencer son commerce</span>
              <h3>Ajoutez votre <span className="ital">activité.</span></h3>
              <p>
                Le référencement est gratuit et tenu par la communauté. Trois étapes,
                quelques jours d'attente pour la vérification.
              </p>
              <div className="ann-cta-actions">
                <a href="#" className="btn btn-primary">
                  <span>Soumettre un commerce</span>
                  <span className="arrow">→</span>
                </a>
                <Link to="/contact" className="btn btn-ghost">
                  <span>Contacter l'équipe</span>
                  <span className="arrow">↗</span>
                </Link>
              </div>
            </div>

            <div className="ann-cta-info">
              <div className="step">
                <b>1</b>
                <p>
                  <b>Soumettre les infos</b>
                  Nom, catégorie, adresse, horaires, photos — environ 5 minutes.
                </p>
              </div>
              <div className="step">
                <b>2</b>
                <p>
                  <b>Vérification</b>
                  Un modérateur passe vérifier sur place sous 7 jours.
                </p>
              </div>
              <div className="step">
                <b>3</b>
                <p>
                  <b>Mise en ligne</b>
                  Votre activité apparaît dans l'annuaire et la carte.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Annuaire;
